/**
 * ConferenceHandler - Manages conference creation via SIP REFER
 *
 * Responsibilities:
 * - Create conference rooms by generating a GUID and SIP INVITE to the room URI
 * - REFER each active/held call into the conference room
 * - Atomic rollback: if any REFER fails, revert all already-processed calls
 * - Support adding calls to an existing conference (reuse existing roomGuid)
 *
 * Follows the legacy PhoneStore.transferToConference pattern adapted to V2 architecture.
 */

import type { CreateConferenceResult } from './index.types';
import { DebugService } from 'Modules/softphone/services/DebugService';
import { EventService } from 'Modules/softphone/services/EventService';
import { SipService } from 'Modules/softphone/services/SipService';
import { WebRTCService } from 'Modules/softphone/services/WebRTCService';
import { useSoftphoneStore } from 'Modules/softphone/store';
import type { CallSession, ConferenceSession } from 'Modules/softphone/types';
import { createLogger } from 'Modules/softphone/utils/logger';
import type { Session } from 'sip.js';
import { SessionState as SipSessionState, UserAgent } from 'sip.js';
import { pushToGTMDataLayer } from 'Utils/analytics';
import { v4 as uuid } from 'uuid';

const logger = createLogger('CallManager:ConferenceHandler');

/** SIP room path prefix used in conference URIs */
const CONFERENCE_ROOM_PREFIX = 'c/room';

/**
 * Emit a diagnostic event without ever being able to affect the merge.
 *
 * Diagnostics observe the conference; they do not participate in it. A failure
 * to record something must never turn into a failed merge for the user.
 */
const emit = (message: string, details: Record<string, unknown>): void => {
  try {
    // The Call channel, not SIP: the room INVITE and the participant REFERs are
    // traced off the socket by SipService. What belongs here is what the merge
    // did — the room it built, who it believes joined, what failed — and saying
    // it twice would show two rows for one message.
    EventService.getInstance().logCallEvent(message, details);
  } catch (error) {
    logger.warn('Failed to record conference event', {
      message,
      error: (error as Error).message,
    });
  }
};

/**
 * Build the SIP URI for the conference room itself (used for the initial INVITE).
 * Includes mintwo=false and endconf=true flags.
 */
export function buildConferenceRoomUri(
  roomGuid: string,
  domain: string
): string {
  return `sip:${CONFERENCE_ROOM_PREFIX}${roomGuid}/noentersound=true,nomoh=true,mintwo=false,endconf=true@${domain}`;
}

/**
 * Build the SIP URI used for REFER-ing individual calls into the conference.
 * Does NOT include mintwo/endconf flags (per legacy pattern).
 */
export function buildConferenceReferUri(
  roomGuid: string,
  domain: string
): string {
  return `sip:${CONFERENCE_ROOM_PREFIX}${roomGuid}/noentersound=true,nomoh=true@${domain}`;
}

export class ConferenceHandler {
  /** Callback for full conference cleanup, injected by CallManager */
  private onConferenceCleanup: () => void = () => {};

  /**
   * Set the cleanup callback. Called by CallManager to wire up
   * cleanupConferenceCompletely() without a circular dependency.
   */
  setCleanupCallback(callback: () => void): void {
    this.onConferenceCleanup = callback;
  }

  /**
   * Create a conference by merging all active/held calls.
   *
   * Algorithm:
   * 1. Validate preconditions (>= 2 calls, no active conference unless adding)
   * 2. Determine roomGuid: reuse existing conference's guid or generate new
   * 3. Create conference session in store with state 'establishing'
   * 4. SIP INVITE to the conference room URI (creates the room on the server)
   * 5. For each call with a valid SIP session, send REFER to the room
   * 6. On success: mark conference as 'active'
   * 7. On failure: atomic rollback of all processed calls
   */
  async createConference(): Promise<CreateConferenceResult> {
    const store = useSoftphoneStore.getState();
    const calls = Array.from(store.activeCalls.values());

    // --- Precondition: at least 2 calls ---
    if (calls.length < 2) {
      pushToGTMDataLayer('call_merge_unavailable');
      throw new Error('Minimum 2 calls required for merge');
    }

    // --- Determine roomGuid ---
    // If a conference already exists, reuse its roomGuid (adding calls scenario)
    const existingConference = store.activeConference;
    const isAddingToExisting = existingConference !== null;
    const roomGuid = isAddingToExisting ? existingConference.roomGuid : uuid();

    // --- Get SIP domain ---
    const domain = this.getWebRtcDomain();

    // --- Filter calls eligible for merge (must have a valid SIP session) ---
    const callsToMerge = calls.filter((c) => c.session !== null);

    if (callsToMerge.length === 0) {
      throw new Error('No calls with valid SIP sessions to merge');
    }

    // --- If not adding to existing, create new conference in store ---
    if (!isAddingToExisting) {
      // Validate no active conference
      if (store.activeConference !== null) {
        throw new Error('A conference is already active');
      }

      const conference: ConferenceSession = {
        id: uuid(),
        roomGuid,
        participantIds: [],
        startTime: new Date().toISOString(),
        conferenceState: 'establishing',
      };
      store.addConference(conference);

      // --- Create the conference room via SIP INVITE ---
      try {
        const roomSession = await this.createConferenceRoom(roomGuid, domain);

        // Store the room session reference for tracking
        store.updateConference({ roomSession });

        // Setup peer connection for the room session so mute works during conference.
        // Keyed by roomGuid so handleMute can find it via the conference.
        const webrtcService = WebRTCService.getInstance();
        webrtcService.setupPeerConnection(roomSession, roomGuid);

        // Register stateChange listener to detect when the room session terminates
        this.registerRoomSessionListener(roomSession, this.onConferenceCleanup);
      } catch (error) {
        logger.error('Failed to create conference room', {
          roomGuid,
          error: (error as Error).message,
        });
        emit('Conference room creation failed', {
          conferenceId: roomGuid,
          error: (error as Error).message,
        });
        store.clearConference();
        pushToGTMDataLayer('call_merge_failed', {
          reason: 'room_creation_failed',
        });
        throw error;
      }
    }

    // --- REFER each call into the conference room ---
    const mergedCallIds: string[] = [];

    for (const call of callsToMerge) {
      // Skip calls already in this conference
      if (call.conferenceId === roomGuid) {
        continue;
      }

      try {
        await this.referCallToConference(call, roomGuid, domain);

        // Update call and conference state
        store.updateCall(call.id, { conferenceId: roomGuid });
        store.addParticipantToConference(call.id);
        mergedCallIds.push(call.id);
        // Locally inferred: the conference server emits no join/leave events,
        // so this is the client's own bookkeeping, not an observed fact.
        emit('Participant added to conference (locally inferred)', {
          callId: call.id,
          conferenceId: roomGuid,
          participantCount: mergedCallIds.length,
        });
      } catch (error) {
        logger.error('REFER failed, initiating rollback', {
          failedCallId: call.id,
          mergedSoFar: mergedCallIds.length,
          error: (error as Error).message,
        });

        // --- Atomic rollback ---
        this.rollbackMergedCalls(mergedCallIds, isAddingToExisting);

        pushToGTMDataLayer('call_merge_failed', {
          reason: 'refer_failed',
          failedCallId: call.id,
        });
        throw error;
      }
    }

    // --- Mark conference as active ---
    store.updateConference({ conferenceState: 'active' });

    // Reset hold/mute state for all merged calls — the conference is a fresh
    // audio context and should not inherit stale per-call states.
    for (const callId of mergedCallIds) {
      store.updateCall(callId, { isOnHold: false, isMuted: false, state: 'active' });
    }

    // Permissions are now derived automatically via selectPermissions selector.
    // No manual re-evaluation needed — updating conferenceId on calls above
    // triggers re-derivation in any component using the selector.

    logger.info('Conference created successfully', {
      roomGuid,
      participantCount: mergedCallIds.length,
      isAddingToExisting,
    });

    pushToGTMDataLayer('call_merge_success', {
      participantCount: mergedCallIds.length,
      conferenceId: roomGuid,
    });

    return { roomGuid, mergedCallIds };
  }

  /**
   * Get the WebRTC domain from SipService config.
   * This mirrors the legacy pattern of reading from configStore.signedInPersonConfig.webRtc.domain.
   */
  private getWebRtcDomain(): string {
    const sipService = SipService.getInstance();
    const config = sipService.getConfig();

    if (!config?.domain) {
      throw new Error('WebRTC domain not available - SIP not configured');
    }

    return config.domain;
  }

  /**
   * Create the conference room by making a SIP INVITE to the room URI.
   * This establishes the conference room on the SIP server.
   * Returns the SIP session for the room so it can be tracked.
   */
  private async createConferenceRoom(
    roomGuid: string,
    domain: string
  ): Promise<Session> {
    const sipService = SipService.getInstance();
    const userAgent = sipService.getUserAgent();

    if (!userAgent) {
      throw new Error('UserAgent not available');
    }

    const roomUri = buildConferenceRoomUri(roomGuid, domain);
    const targetUri = UserAgent.makeURI(roomUri);

    if (!targetUri) {
      throw new Error(`Invalid conference room URI: ${roomUri}`);
    }

    logger.info('Creating conference room', { roomGuid, roomUri });
    // The room URI's parameters (noentersound, nomoh, mintwo, endconf) are
    // themselves diagnostic — they decide how the bridge behaves.
    emit('Conference room requested', { conferenceId: roomGuid, roomUri });

    const { Inviter } = await import('sip.js');
    const inviter = new Inviter(userAgent, targetUri);

    // Log session state transitions (also captures rejections)
    inviter.stateChange.addListener((state) => {
      logger.debug('Conference room inviter state change', { state });
      emit(`Conference room session ${String(state)}`, {
        conferenceId: roomGuid,
        state: String(state),
      });
      if (state === SipSessionState.Terminated) {
        logger.warn('Conference room session terminated (possibly rejected)');
      }
    });

    await inviter.invite();

    // Wait for the session to be fully established (200 OK received and ACK sent).
    // inviter.invite() resolves when the INVITE is sent, NOT when the session is established.
    // Without this, REFERs can be sent before the room actually exists on the server,
    // causing a race condition if the INVITE fails (e.g. 500 error after auth retry).
    await this.waitForSessionEstablished(inviter);

    logger.info('Conference room created', { roomGuid });
    emit('Conference room established', {
      conferenceId: roomGuid,
      roomUri,
    });

    return inviter;
  }

  /**
   * Wait for a SIP session to reach the Established state.
   * Rejects if the session transitions to Terminated instead.
   */
  private waitForSessionEstablished(session: Session): Promise<void> {
    // If already established, resolve immediately
    if (session.state === SipSessionState.Established) {
      return Promise.resolve();
    }

    // If already terminated, reject immediately
    if (session.state === SipSessionState.Terminated) {
      return Promise.reject(
        new Error('Conference room session terminated before establishing')
      );
    }

    return new Promise<void>((resolve, reject) => {
      const onStateChange = (state: SipSessionState) => {
        if (state === SipSessionState.Established) {
          session.stateChange.removeListener(onStateChange);
          resolve();
        } else if (state === SipSessionState.Terminated) {
          session.stateChange.removeListener(onStateChange);
          reject(
            new Error('Conference room session terminated before establishing')
          );
        }
      };
      session.stateChange.addListener(onStateChange);
    });
  }

  /**
   * Send a SIP REFER to redirect a call into the conference room.
   */
  private async referCallToConference(
    call: CallSession,
    roomGuid: string,
    domain: string
  ): Promise<void> {
    if (!call.session) {
      throw new Error(`Call ${call.id} has no SIP session`);
    }

    const referUriStr = buildConferenceReferUri(roomGuid, domain);
    const referUri = UserAgent.makeURI(referUriStr);

    if (!referUri) {
      throw new Error(`Invalid REFER URI: ${referUriStr}`);
    }

    logger.debug('Sending REFER to conference', {
      callId: call.id,
      referUri: referUriStr,
    });
    emit('Participant referred to conference room', {
      callId: call.id,
      conferenceId: roomGuid,
      referUri: referUriStr,
    });

    try {
      await call.session.refer(referUri);
    } catch (error) {
      emit('Participant referral failed', {
        callId: call.id,
        conferenceId: roomGuid,
        error: (error as Error).message,
      });
      throw error;
    }

    logger.debug('REFER sent successfully', { callId: call.id });
    emit('Participant referral accepted', {
      callId: call.id,
      conferenceId: roomGuid,
    });
  }

  /**
   * Rollback all calls that were already merged.
   * Removes conferenceId from each call and clears the conference if it was newly created.
   */
  private rollbackMergedCalls(
    mergedCallIds: string[],
    isAddingToExisting: boolean
  ): void {
    const store = useSoftphoneStore.getState();

    logger.info('Rolling back merged calls', {
      callCount: mergedCallIds.length,
      isAddingToExisting,
    });
    emit('Conference merge rolled back', {
      rolledBackCallIds: mergedCallIds,
      isAddingToExisting,
    });

    // Revert conferenceId on all already-processed calls
    for (const callId of mergedCallIds) {
      store.updateCall(callId, { conferenceId: null });
    }

    // If this was a new conference (not adding to existing), clear it entirely
    if (!isAddingToExisting) {
      store.clearConference();
    }
  }

  /**
   * Capture the conference room itself as a diagnostic record.
   *
   * The room carries the audio for the whole conference and has its own peer
   * connection — already keyed by roomGuid in WebRTCService — so its SDP, ICE
   * candidates and connection states are what a "no audio in the conference"
   * report actually needs. Without this, only the participant legs were ever
   * recorded and the thing they were all connected to was invisible.
   */
  private captureRoomRecord(): void {
    try {
      const store = useSoftphoneStore.getState();
      const conference = store.activeConference;
      if (!conference) {
        return;
      }

      const participantIds = [...conference.participantIds];

      DebugService.getInstance().capture(conference.roomGuid, {
        phoneNumber: `conference:${conference.roomGuid}`,
        direction: 'outbound',
        state: 'ended',
        startTime: conference.startTime,
        answerTime: conference.startTime,
        endTime: new Date().toISOString(),
        scenario: 'conference-room',
        interactionId: conference.roomGuid,
        relatedCallIds: participantIds,
        conference: {
          roomGuid: conference.roomGuid,
          roomUri: buildConferenceRoomUri(
            conference.roomGuid,
            this.getWebRtcDomain()
          ),
          participantIds,
          participantsAreLocallyInferred: true,
        },
      });
    } catch (error) {
      logger.warn('Failed to capture conference room diagnostics', {
        error: (error as Error).message,
      });
    }
  }

  /**
   * Register a stateChange listener on the room session to detect when it terminates.
   * Delegates full conference cleanup to the provided callback (CallManager.cleanupConferenceCompletely).
   */
  private registerRoomSessionListener(
    session: Session,
    onCleanup: () => void
  ): void {
    session.stateChange.addListener((state) => {
      if (state === SipSessionState.Terminated) {
        logger.info('Conference room session terminated, delegating cleanup');
        emit('Conference room session terminated', {});
        this.captureRoomRecord();
        onCleanup();
      }
    });
  }
}
